import * as React from 'react';
import { useQuery } from 'react-query';
import axiosClient from '@lib/utils/axiosClient';
import { compact, filter, find, get, map } from 'lodash';
import moment from 'moment';
import { Audit } from 'deprecated/fragments/types/auditType';

type Params = {
  endDate?: string;
  modelId: string;
  startDate?: string;
};

interface AuditsReturn {
  audits: Audit[] | null;
  queryStatus: 'loading' | 'idle' | 'error' | 'success';
}

const useAuditsMany = (params: Params): AuditsReturn => {
  const stringParams = new URLSearchParams(params).toString();
  const requestKey = `audits?${stringParams}`;

  const request = React.useCallback(() => axiosClient.get<Audit[]>(requestKey), [requestKey]);
  const allAuditsQuery = useQuery(requestKey, request);
  const allAudits = React.useMemo(() => (allAuditsQuery.isSuccess ? allAuditsQuery.data?.data : []), [allAuditsQuery]);

  const auditsWithDescriptions = React.useMemo(() => compact(filter(allAudits, { modelField: 'versionDescription' })), [
    allAudits,
  ]);

  const audits = React.useMemo(() => {
    const auditsFiltered = compact(filter(allAudits, { modelField: 'template' }));
    return map(auditsFiltered, (audit) => {
      // Obtain audit with the versionDescription field updated for a specific template change
      const auditWithDescription = find(
        auditsWithDescriptions,
        ({ timestamp }) =>
          !!audit.timestamp &&
          timestamp &&
          moment(audit.timestamp).isBetween(
            moment(timestamp).subtract(1, 'second'),
            moment(timestamp).add(1, 'second'),
          ),
      );
      return {
        ...audit,
        _id: Math.random().toString(),
        description: get(auditWithDescription, 'new', ''),
      };
    });
  }, [allAudits, auditsWithDescriptions]);

  return { audits, queryStatus: allAuditsQuery.status };
};

export default useAuditsMany;
